import React from "react";
import Utils from "../../utility";
import Calender from "./calender";
import PropTypes from "prop-types";

/**
 * Renders a date range selector component.
 *
 * @param {object} dates - An object containing the start and end dates.
 * @param {function} setDates - A function to update the start and end dates.
 * @return {JSX.Element} The date range selector component.
 */
const SelectDateRange = ({
  dates,
  setDates,
  placeHolder,
  calenderOpen,
  setCalenderOpen,
  calendarDropDownRef,
  id,
  setPage,
  setIsFilterApplied,
  setIsApplyCount,
  isApplyCount
}) => {
  /**
   * Updates the dates based on the selected date and position.
   *
   * @param {Date} selectedDate - The selected date.
   * @param {string} position - The position ('start' or 'end').
   * @return {void}
   */
  const handleDateChange = (selectedDate, position) => {
    if (selectedDate && position === "start") {
      setDates((pre) => ({
        start: selectedDate?.toDate().setHours(0, 0, 0, 0),
        end: pre.end
      }));
    } else {
      setDates((pre) => ({
        end: selectedDate?.toDate().setHours(23, 59, 59, 999),
        start: pre.start
      }));
    }
  };
  /**
   * Sets the calender open state.
   *
   * @return {void}
   */
  const setCalender = () => {
    setCalenderOpen(!calenderOpen);
  };

  const applyDatesHandler = () => {
    setIsApplyCount(isApplyCount + 1);
    setCalenderOpen(!calenderOpen);
    if (id === "providedConsent") {
      setPage("page", 1);
    } else if (id === "emailDatabase") {
      setPage(1);
    }
    setIsFilterApplied(true);
  };

  /**
   * Clears the dates by setting start and end to null.
   *
   * @return {void}
   */
  const clearDates = () => {
    setIsApplyCount(0);
    setDates({
      start: null,
      end: null
    });

    if (id === "providedConsent") {
      setPage("page", 1);
    } else if (id === "emailDatabase") {
      setPage(1);
    }
    setIsFilterApplied(false);
  };

  const formattedDates = () => {
    const startDate = dates?.start ? Utils.dateFormat(dates.start) : "";
    const endDate = dates?.end ? ` - ${Utils.dateFormat(dates.end)}` : "";
    return startDate + endDate;
  };

  return (
    <div
      ref={calendarDropDownRef}
      className={`flex w-full flex-row gap-3 ${dates?.start && dates?.end ? "md:w-auto" : "md:w-241px"}`}
    >
      <div className="relative flex h-10 w-full cursor-pointer flex-row items-center justify-between gap-2 rounded border border-grey-D9D9D9 outline-none">
        <div className="flex w-full items-center px-3" onClick={setCalender}>
          <input
            className="w-full cursor-pointer font-MontserratMedium text-ft15-22.5-0.3 text-black-000000 outline-none placeholder:text-black-000000 "
            readOnly
            value={formattedDates()}
            placeholder={placeHolder}
          />
          <span
            className={`${(dates?.start || dates?.end) && "hidden"} pr-2 font-MontserratMedium text-ft15-26.25 text-black-000000 `}
          >
            All
          </span>
          <img
            className={` text-purple-6C36B0 ${
              calenderOpen && "rotate-180"
            } duration-100`}
            src="/images/dropdown-arrow-purple.svg"
          />
        </div>
        {calenderOpen && (
          <div className="absolute bottom-0 left-0 top-10 z-10 mt-9px">
            <div className="h-80 overflow-auto rounded border border-grey-D9D9D9 bg-white px-10 py-5 xl:h-auto ">
              <div className="flex flex-col gap-10 xl:flex-row">
                <Calender
                  date={dates?.start}
                  handleDateChange={handleDateChange}
                  inputPlaceHolder={"Start Date"}
                  position={"start"}
                  maximumDate={Date.now()}
                  miniumDate={""}
                />
                <Calender
                  date={dates?.end}
                  handleDateChange={handleDateChange}
                  inputPlaceHolder={"End Date"}
                  position={"end"}
                  maximumDate={Date.now()}
                  miniumDate={dates?.start ? dates?.start : Date.now()}
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={applyDatesHandler}
                  className="mt-4 rounded border border-grey-D9D9D9 px-2 py-1 font-MontserratMedium text-ft15-26.25 text-black-000000"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {dates?.start !== null && (
        <button
          onClick={clearDates}
          className="font-PoppinsRegular text-ft15-22.5-0.2 text-purple-6C36B0"
        >
          Clear
        </button>
      )}
    </div>
  );
};
SelectDateRange.propTypes = {
  dates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  setDates: PropTypes.func.isRequired,
  placeHolder: PropTypes.string,
  calenderOpen: PropTypes.bool.isRequired,
  setCalenderOpen: PropTypes.func.isRequired,
  calendarDropDownRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf() })
  ]),
  id: PropTypes.string,
  setPage: PropTypes.func.isRequired,
  setIsFilterApplied: PropTypes.func.isRequired,
  setIsApplyCount: PropTypes.func.isRequired,
  isApplyCount: PropTypes.bool
};

export default SelectDateRange;
